/**
 * Office / PDF document block editor (URL + upload)
 */

import * as React from 'react';
import { cn, Label, Input, Button } from '@ai-content-hub/content-kit';
import { uploadMedia } from '@ai-content-hub/content-core';
import { capLimitBytes, useUploadLimits } from '@ai-content-hub/shared-config';
import { Upload } from 'lucide-react';
import type { OfficeDocumentBlockContent } from '../block/types';
import { useElementUITranslations } from '../i18n/elementUI';

const OFFICE_ACCEPT_TYPES = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
];

const OFFICE_MAX_BYTES = 50 * 1024 * 1024;

export interface OfficeDocumentEditProps {
    value: OfficeDocumentBlockContent;
    onChange: (value: OfficeDocumentBlockContent) => void;
    disabled?: boolean;
    className?: string;
}

export const OfficeDocumentEdit: React.FC<OfficeDocumentEditProps> = ({
    value,
    onChange,
    disabled = false,
    className,
}) => {
    const fileRef = React.useRef<HTMLInputElement>(null);
    const et = useElementUITranslations();
    const [uploading, setUploading] = React.useState(false);
    // EAA-1212: the file goes to the media endpoint, so its 50MB cap is lowered to the
    // server's configured media cap when that is smaller. The MIME list stays: the
    // viewer renders only these formats, not every "document" extension the server takes.
    const { mediaMaxSizeMb } = useUploadLimits();
    const maxSize = capLimitBytes(OFFICE_MAX_BYTES, mediaMaxSizeMb);

    const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file || disabled) return;

        try {
            setUploading(true);
            const result = await uploadMedia(file, {
                maxSize,
                acceptedTypes: OFFICE_ACCEPT_TYPES,
            });
            if (result.success && result.url) {
                onChange({
                    ...value,
                    src: result.url,
                    fileName: file.name,
                });
            }
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className={cn('office-document-edit space-y-4', className)}>
            <div className="space-y-2">
                <Label htmlFor="office-doc-title">{et.titleOptional}</Label>
                <Input
                    id="office-doc-title"
                    value={value.title ?? ''}
                    onChange={(e) => onChange({ ...value, title: e.target.value })}
                    disabled={disabled}
                    placeholder={et.documentTitlePlaceholder}
                    className="office-document-edit-title bg-muted/40 transition-colors duration-200 focus-visible:bg-background"
                />
            </div>
            <div className="space-y-2">
                <Label htmlFor="office-doc-url">
                    {et.documentUrl} <span className="text-destructive">*</span>
                </Label>
                <Input
                    id="office-doc-url"
                    value={value.src}
                    onChange={(e) => onChange({ ...value, src: e.target.value })}
                    disabled={disabled}
                    placeholder="https://… (PDF or Office file)"
                    className="office-document-edit-url bg-muted/40 transition-colors duration-200 focus-visible:bg-background"
                />
                <input
                    ref={fileRef}
                    type="file"
                    className="sr-only"
                    accept={OFFICE_ACCEPT_TYPES.join(',')}
                    disabled={disabled || uploading}
                    onChange={handleUpload}
                />
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={disabled || uploading}
                    className="office-document-edit-upload mt-2 cursor-pointer"
                    onClick={() => fileRef.current?.click()}
                >
                    <Upload className="h-4 w-4 mr-2" />
                    {uploading ? et.uploading : et.uploadFile}
                </Button>
                {value.fileName ? (
                    <p className="text-xs text-muted-foreground office-document-edit-filename">
                        {value.fileName}
                    </p>
                ) : null}
            </div>
        </div>
    );
};
